import Cookies from "js-cookie";
import { useUserStoreHook } from "@/store/modules/user";
import { storageLocal, isString, isIncludeAllChildren } from "@pureadmin/utils";
import { refreshTokenApi } from "@/api/user";

export interface DataInfo<T> {
  /** token */
  accessToken: string;
  /** `accessToken`的过期时间（时间戳） */
  expires: T;
  /** 用于调用刷新accessToken的接口时所需的token */
  refreshToken: string;
  /** 用户ID */
  id?: string;
  /** 头像 */
  avatar?: string;
  /** 用户账号 */
  userCode?: string;
  /** 用户名 */
  userName?: string;
  /** 用户类型 */
  accountType?: string;
  /** 公司 */
  companyName?: string;
  /** 公司ID */
  companyId?: string;
  /** 当前登录用户的角色 */
  roles?: Array<string>;
  /** 当前登录用户的按钮级别权限 */
  permissions?: Array<string>;
}

export const userKey = "user-info";
export const TokenKey = "authorized-token-vnet";
/**
 * 通过`multiple-tabs`是否在`cookie`中，判断用户是否已经登录系统，
 * 从而支持多标签页打开已经登录的系统后无需再登录。
 * 浏览器完全关闭后`multiple-tabs`将自动从`cookie`中销毁，
 * 再次打开浏览器需要重新登录系统
 * */
export const multipleTabsKey = "multiple-tabs";

/** 获取`token` */
export function getToken(): DataInfo<number> {
  // 此处与`TokenKey`相同，此写法解决初始化时`Cookies`中不存在`TokenKey`报错
  return Cookies.get(TokenKey)
    ? JSON.parse(Cookies.get(TokenKey))
    : storageLocal().getItem(userKey);
}

/**
 * @description 设置`token`以及一些必要信息并采用无感刷新`token`方案
 * 无感刷新：后端返回`accessToken`（访问接口使用的`token`）、`refreshToken`（用于调用刷新`accessToken`的接口时所需的`token`，`refreshToken`的过期时间（比如30天）应大于`accessToken`的过期时间（比如2小时））、`expires`（`accessToken`的过期时间）
 * 将`accessToken`、`expires`、`refreshToken`这三条信息放在key值为authorized-token的cookie里（过期自动销毁）
 * 将`id`、`avatar`、`userCode`、`userName`、`roles`、`permissions`、`refreshToken`、`expires`这七条信息放在key值为`user-info`的localStorage里（利用`multipleTabsKey`当浏览器完全关闭后自动销毁）
 */
export function setToken(data: DataInfo<number>) {
  let expires = 0;
  const { accessToken, refreshToken } = data;
  const { isRemembered, loginDay } = useUserStoreHook();
  expires = data.expires; // 如果后端直接设置时间戳，将此处代码改为expires = data.expires，然后把上面的DataInfo<Date>改成DataInfo<number>即可
  const cookieString = JSON.stringify({ accessToken, expires, refreshToken });

  expires > 0
    ? Cookies.set(TokenKey, cookieString, {
        expires: (expires - Date.now()) / 86400000
      })
    : Cookies.set(TokenKey, cookieString);

  Cookies.set(
    multipleTabsKey,
    "true",
    isRemembered
      ? {
          expires: loginDay
        }
      : {}
  );

  function setUserKey({
    id,
    avatar,
    userCode,
    userName,
    companyName,
    companyId,
    accountType,
    roles,
    permissions
  }) {
    useUserStoreHook().SET_USERID(id);
    useUserStoreHook().SET_AVATAR(avatar);
    useUserStoreHook().SET_USERCODE(userCode);
    useUserStoreHook().SET_USERNAME(userName);
    useUserStoreHook().SET_COMPANYNAME(companyName);
    useUserStoreHook().SET_COMPANYID(companyId);
    useUserStoreHook().SET_ACCOUNTTYPE(accountType);
    useUserStoreHook().SET_ROLES(roles);
    useUserStoreHook().SET_PERMS(permissions);
    storageLocal().setItem(userKey, {
      id,
      refreshToken,
      expires,
      userCode,
      userName,
      companyName,
      companyId,
      accountType,
      roles,
      permissions
    });
  }

  if (data.id && data.userCode && data.roles) {
    const { id, userCode, roles } = data;
    setUserKey({
      id,
      userCode,
      avatar: data?.avatar ?? "",
      userName: data?.userName ?? "",
      companyName: data?.companyName ?? "",
      companyId: data?.companyId ?? "",
      accountType: data?.accountType ?? "",
      roles,
      permissions: data?.permissions ?? []
    });
  } else {
    const id = storageLocal().getItem<DataInfo<number>>(userKey)?.id ?? "";
    const userCode =
      storageLocal().getItem<DataInfo<number>>(userKey)?.userCode ?? "";
    const avatar =
      storageLocal().getItem<DataInfo<number>>(userKey)?.avatar ?? "";
    const userName =
      storageLocal().getItem<DataInfo<number>>(userKey)?.userName ?? "";
    const companyName =
      storageLocal().getItem<DataInfo<number>>(userKey)?.companyName ?? "";
    const companyId =
      storageLocal().getItem<DataInfo<number>>(userKey)?.companyId ?? "";
    const accountType =
      storageLocal().getItem<DataInfo<number>>(userKey)?.accountType ?? "";
    const roles =
      storageLocal().getItem<DataInfo<number>>(userKey)?.roles ?? [];
    const permissions =
      storageLocal().getItem<DataInfo<number>>(userKey)?.permissions ?? [];
    setUserKey({
      id,
      avatar,
      userCode,
      userName,
      companyName,
      companyId,
      accountType,
      roles,
      permissions
    });
  }
}

/** 删除`token`以及key值为`user-info`的localStorage信息 */
export function removeToken() {
  Cookies.remove(TokenKey);
  Cookies.remove(multipleTabsKey);
  storageLocal().removeItem(userKey);
}

/** 格式化token（jwt格式） */
export const formatToken = (token: string): string => {
  return "Bearer " + token;
};

/** 是否有按钮级别的权限（根据登录接口返回的`permissions`字段进行判断）*/
export const hasPerms = (value: string | Array<string>): boolean => {
  if (!value) return false;
  const allPerms = "*:*:*";
  const { permissions } = useUserStoreHook();
  if (!permissions) return false;
  if (permissions.length === 1 && permissions[0] === allPerms) return true;
  const isAuths = isString(value)
    ? permissions.includes(value)
    : isIncludeAllChildren(value, permissions);
  return isAuths ? true : false;
};

/** 获取 `accessToken` */
export const getAccessToken = async () => {
  let data = getToken();
  // console.log(data);
  if (!data.accessToken) {
    const res = await refreshTokenApi({
      refreshToken: data.refreshToken
    });
    data = res.data;
    setToken(res.data);
  }
  // console.log(data);
  return formatToken(data.accessToken);
};
